<template>
  <section class="policy-docs-page">
    <div class="pd-container">
      <!-- Hero -->
      <header class="pd-hero">
        <div class="pd-hero-copy">
          <p class="pd-eyebrow">Policy documents</p>
          <h1>
            We’ve simplified our policies and removed any unnecessary jargon.
            Read on.
          </h1>
          <p class="pd-lead">
            Below are links to all our policy documents. Simply click on a link
            to view the document.
          </p>
        </div>
        <div class="pd-hero-media">
          <img
            src="~/assets/images/illustrations/Policy-page.svg"
            alt="Illustration of a family riding together"
          />
        </div>
      </header>

      <!-- Documents, one section per policy period (newest first) -->
      <section v-for="period in periods" :key="period.scope" class="pd-period">
        <div class="pd-section-head">
          <h2>{{ period.heading }}</h2>
          <p class="pd-scope">{{ period.scope }}</p>
        </div>

        <ul class="pd-grid">
          <li v-for="doc in period.documents" :key="doc.href" :class="doc.span">
            <a
              :href="doc.href"
              target="_blank"
              rel="noopener"
              class="pd-card"
              :aria-label="`${doc.title} — open PDF, ${doc.size}`"
            >
              <span class="pd-card-icon">
                <img :src="doc.icon" alt="" aria-hidden="true" />
              </span>
              <span class="pd-card-body">
                <span class="pd-card-title">
                  {{ doc.title }}
                  <span v-if="doc.badge" class="pd-badge">{{ doc.badge }}</span>
                </span>
                <span class="pd-card-desc">{{ doc.desc }}</span>
                <span class="pd-card-meta">PDF · {{ doc.size }}</span>
              </span>
              <span class="pd-card-action" aria-hidden="true">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor">
                  <path
                    d="M10 4v9m0 0 3.5-3.5M10 13 6.5 9.5M4 16h12"
                    stroke-width="1.8"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
            </a>
          </li>
        </ul>
      </section>

      <!-- Documents for earlier policy periods are no longer listed here;
           the team sends them on request. -->
      <aside class="pd-note">
        <p>
          Looking for documents for a policy that started before
          <strong>30 August 2024</strong>?<br />
          <nuxt-link to="/contact-us">Contact our team</nuxt-link> and we’ll
          send them over.
        </p>
        <nuxt-link to="/contact-us" class="button is-secondary">
          Contact us
        </nuxt-link>
      </aside>
    </div>
  </section>
</template>

<script>
import { buildSocialMeta } from '@/helpers/social-meta'

const CANONICAL_PATH = '/policy-information'

// File sizes are written by hand — update them if a PDF is replaced.
const POLICY_DOC = {
  title: 'Policy',
  desc: 'Full wording — what’s covered and what isn’t',
  icon: require('~/assets/images/icons/blue/policy.svg'),
}

const IPID_DOC = {
  title: 'IPID',
  badge: 'Insurance Product Information Document',
  desc: 'A short summary of your cover — start here if you only read one',
  icon: require('~/assets/images/icons/blue/IPID-bike.svg'),
  span: 'is-full',
}

const LOCK_LIST_DOC = {
  title: 'Approved Lock List',
  desc: 'Locks that meet your policy’s security requirement',
  icon: require('~/assets/images/icons/blue/approved-lock.svg'),
}

const TERMS_DOC = {
  title: 'Terms & Conditions',
  desc: 'The agreement between you and Pedalsure',
  icon: require('~/assets/images/icons/blue/terms-and-conditions.svg'),
}

// The privacy policy was not reissued for the 11/26 wording: the 11-2026 file
// is a copy of the August 2024 one, so each period has a complete set.
const PRIVACY_DOC = {
  title: 'Privacy Policy',
  desc: 'How we handle and store your data',
  size: '233 KB',
  icon: require('~/assets/images/icons/blue/terms-and-conditions.svg'),
}

const PERIODS = [
  {
    heading: 'Current documents',
    scope: 'Policies starting on or after 1 November 2026',
    documents: [
      {
        ...POLICY_DOC,
        size: '830 KB',
        href: '/documents/november_2026/Pedalsure_Policy_Document_11-2026.pdf',
      },
      {
        ...TERMS_DOC,
        size: '342 KB',
        href: '/documents/november_2026/Terms_&_Conditions_11-2026.pdf',
      },
      {
        ...PRIVACY_DOC,
        href: '/documents/november_2026/Privacy_Policy_11-2026.pdf',
      },
      {
        ...LOCK_LIST_DOC,
        size: '209 KB',
        href: '/documents/november_2026/Approved_Lock_List_11-2026.pdf',
      },
      {
        ...IPID_DOC,
        size: '328 KB',
        href: '/documents/november_2026/Pedalsure_IPID_11-2026.pdf',
      },
    ],
  },
  {
    heading: 'Previous documents',
    scope: 'Policies starting 30 August 2024 – 31 October 2026',
    documents: [
      {
        ...POLICY_DOC,
        size: '672 KB',
        href: '/documents/august_2024/Policy Document 08-24.pdf',
      },
      {
        ...TERMS_DOC,
        size: '235 KB',
        href: '/documents/august_2024/Terms & Conditions 08-24.pdf',
      },
      {
        ...PRIVACY_DOC,
        href: '/documents/august_2024/Privacy Policy 08-24.pdf',
      },
      {
        ...LOCK_LIST_DOC,
        size: '1.4 MB',
        href: '/documents/august_2024/Approved Locks and Security Requirements.pdf',
      },
      {
        ...IPID_DOC,
        size: '116 KB',
        href: '/documents/august_2024/IPID 08-24.pdf',
      },
    ],
  },
]

export default {
  name: 'PolicyInformation',
  data() {
    return { periods: PERIODS }
  },
  head() {
    const title = 'Bicycle Insurance Policy Information | PedalSure'
    const description =
      'Our bicycle insurance policy information gives you full detail of the cover you are considering. Learn about the key aspects of your cover and its terms and conditions.'
    const keywords = 'bicycle insurance policy'
    return {
      title,
      meta: [
        { hid: 'description', name: 'description', content: description },
        { hid: 'keywords', name: 'keywords', content: keywords },
        ...buildSocialMeta({
          title,
          description,
          path: CANONICAL_PATH,
        }).meta,
      ],
    }
  },
}
</script>

<style lang="scss" scoped>
.pd-container {
  max-width: 1120px;
  margin: 0 auto;
  padding: 24px 20px 80px;

  @include tablet {
    padding: 40px 32px 100px;
  }
}

/* ---- Hero ------------------------------------------------------------ */
.pd-hero {
  display: grid;
  gap: 32px;
  align-items: center;
  margin-bottom: 64px;

  @include desktop {
    grid-template-columns: 1fr 1fr;
    gap: 56px;
    margin-bottom: 88px;
  }
}

.pd-eyebrow {
  font-size: $ps-text-label;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: $ps-muted;
  margin-bottom: 16px;
}

.pd-lead {
  margin-top: 20px;
  max-width: 34em;
  font-size: $ps-text-body;
  line-height: 1.6;
  color: $ps-body-color;
}

.pd-hero-media img {
  display: block;
  width: 100%;
  height: auto;
  border-radius: $ps-radius-lg;
}

/* ---- Section heading ------------------------------------------------- */
.pd-section-head {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 24px;

  @include tablet {
    flex-direction: row;
    align-items: baseline;
    justify-content: space-between;
    gap: 24px;
  }
}

.pd-period + .pd-period {
  margin-top: 56px;
}

.pd-scope {
  font-size: $ps-text-label;
  font-weight: 600;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: $ps-muted;
}

/* ---- Document cards -------------------------------------------------- */
.pd-grid {
  display: grid;
  gap: 16px;
  list-style: none;
  margin: 0;
  padding: 0;

  @include tablet {
    grid-template-columns: repeat(2, 1fr);
  }

  .is-full {
    @include tablet {
      grid-column: 1 / -1;
    }
  }
}

.pd-card {
  display: flex;
  align-items: center;
  gap: 16px;
  height: 100%;
  padding: 20px;
  border: 1.5px solid $ps-border-color;
  border-radius: $ps-radius-lg;
  background: #fff;
  color: inherit;
  transition: border-color 120ms ease, box-shadow 120ms ease;

  &:hover {
    border-color: $ps-primary-200;
    box-shadow: 0 6px 20px rgba(20, 22, 31, 0.08);
  }

  &:focus-visible {
    outline: none;
    border-color: $ps-primary;
    box-shadow: 0 0 0 4px $ps-primary-50;
  }
}

.pd-card-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: $ps-radius-md;
  background: $ps-primary-50;

  img {
    width: 30px;
    height: 30px;
  }
}

.pd-card-body {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.pd-card-title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: $ps-text-body;
  font-weight: 600;
  line-height: 1.3;
  color: $ps-ink;
}

.pd-badge {
  display: inline-block;
  padding: 3px 10px;
  border-radius: $ps-radius-pill;
  background: $ps-primary-50;
  color: $ps-primary;
  font-size: $ps-text-label;
  font-weight: 600;
  letter-spacing: 0;
  text-transform: none;
}

.pd-card-desc {
  font-size: $ps-text-small;
  line-height: 1.45;
  color: $ps-body-color;
}

.pd-card-meta {
  font-size: $ps-text-small;
  color: $ps-muted;
}

.pd-card-action {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: $ps-tap-min;
  height: $ps-tap-min;
  border-radius: $ps-radius-md;
  background: $ps-primary-50;
  color: $ps-primary;

  svg {
    width: 20px;
    height: 20px;
  }
}

/* ---- Older-policies note --------------------------------------------- */
.pd-note {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  margin-top: 32px;
  padding: 24px;
  border-radius: $ps-radius-lg;
  background: $ps-surface;

  @include tablet {
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 32px;
    padding: 28px 32px;
  }

  p {
    font-size: $ps-text-body;
    line-height: 1.6;
    color: $ps-body-color;
  }

  a:not(.button) {
    color: $ps-primary;
    font-weight: 600;
    text-decoration: underline;
  }

  .button {
    flex: 0 0 auto;
  }
}
</style>
